import React from "react";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import type { IUploadStore } from "../../../UploadStore";
import { injectFromAppUpload } from "../../../inject";
import { StaticNotificationComponent } from "../../../../shared-components/static-notification/StaticNotificationComponent";
import type { StudyInUpload } from "../../../StudyInUpload";
import { NonDicomFileComponent } from "./NonDicomFileComponent";
import { RichToolTipComponent } from "../../../../shared-components/rich-tooltip/RichToolTipComponent";
import { DobButton, DobSelect } from "@dedalusdiit/dob-components";

interface IArrangeSupportedNonDicomFilesComponentProps {
    resourcesStore?: IResourcesStore;
    uploadStore?: IUploadStore;
}

interface IArrangeSupportedNonDicomFilesComponentState {
    selected: number;
}

@injectFromAppUpload<IArrangeSupportedNonDicomFilesComponentProps>(stores => ({
    uploadStore: stores.uploadStore,
    resourcesStore: stores.resourcesStore,
}))
@observer
export class ArrangeSupportedNonDicomFilesComponent extends React.Component<IArrangeSupportedNonDicomFilesComponentProps, IArrangeSupportedNonDicomFilesComponentState> {
    state = {
        selected: 0,
    };

    private readonly addSelectedFilesToStudy = (selectedStudy: StudyInUpload) => {
        const { uploadStore } = this.props;
        if (selectedStudy) {
            uploadStore!.addAllSelectedNonDICOMFilesToArrangeToStudy(selectedStudy);
            this.setState(prevState => ({
                selected: prevState.selected + 1,
            }));
        }
    };

    private readonly removeSelectedFiles = () => {
        const { uploadStore } = this.props;
        uploadStore!.removeAllSelectedNonDICOMFiles();
    };

    private readonly toggleSelectAllFiles = () => {
        const { uploadStore } = this.props;
        const { supportedNonDICOMFilesToArrange } = uploadStore!;
        if (supportedNonDICOMFilesToArrange.some(s => !s.selected)) {
            //At least one is not selected yet, select remaining
            supportedNonDICOMFilesToArrange
                .filter(s => !s.selected)
                .forEach(file => {
                    file.setSelected(true);
                });
        }
        //All are selected, deselect all
        else {
            supportedNonDICOMFilesToArrange.forEach(file => {
                file.setSelected(false);
            });
        }
    };

    customItemRendererForStudy = ({
        option,
        resources,
        selectedNonDICOMFilesLength,
        maxNonDicomFilesAddedToAStudy,
    }: {
        option: StudyInUpload;
        resources: IResources;
        selectedNonDICOMFilesLength: number;
        maxNonDicomFilesAddedToAStudy: number;
    }) => (
        <RichToolTipComponent
            toolTipPosition={"right"}
            toolTipContent={
                <div>
                    {resources.CurrentlyAddedXNonDICOMFiles.replace("{0}", option.nonDicomFiles.length.toString())}.
                    <br />
                    {option.nonDicomFiles.length + selectedNonDICOMFilesLength > maxNonDicomFilesAddedToAStudy
                        ? resources.WouldSurpassTheMaximumAmountOfXNonDICOMFiles.replace("{0}", maxNonDicomFilesAddedToAStudy.toString()) + "."
                        : null}
                </div>
            }
            className={"dob-select-item" + (option.nonDicomFiles.length + selectedNonDICOMFilesLength > maxNonDicomFilesAddedToAStudy ? " too-many-files-selected" : "")}
        >
            {option.label}
        </RichToolTipComponent>
    );

    render() {
        const { resourcesStore, uploadStore } = this.props;
        const { resources } = resourcesStore!;
        const { supportedNonDICOMFilesToArrange, patientsFound, allStudiesThatAreNotUploadedYet, selectedNonDICOMFiles, maxNonDicomFilesAddedToAStudy } = uploadStore!;

        //Nothing to arrange
        if (supportedNonDICOMFilesToArrange.length === 0) return null;

        //No patients / studies yet
        if (patientsFound.length === 0) {
            return (
                <StaticNotificationComponent
                    type={"error"}
                    customClass={"files-to-arrange-notification"}
                    title={resources.YouCanOnlyUploadNonDICOMFilesIfYouUploadAtLeast1DICOMFile}
                >
                    <div className={"files-to-arrange-container"}>
                        {supportedNonDICOMFilesToArrange.map(f => (
                            <NonDicomFileComponent file={f} key={f.fileGuid} canBeRemoved={true} />
                        ))}
                    </div>
                </StaticNotificationComponent>
            );
        }

        return (
            <StaticNotificationComponent type={"step"} customClass={"files-to-arrange-notification"} title={resources.AddFilesToAStudy}>
                <h3>{resources.EachStudyCanHaveUpToXNonDICOMFiles.replace("{0}", maxNonDicomFilesAddedToAStudy.toString())}</h3>
                <div className={"files-to-arrange-container"}>
                    {supportedNonDICOMFilesToArrange.map(f => (
                        <NonDicomFileComponent file={f} key={f.fileGuid} canBeRemoved={true} />
                    ))}
                </div>
                <div className={"move-all-to-study-container"}>
                    <p className={"files-selected"}>{resources.XSelectedFiles.replace("{0}", selectedNonDICOMFiles.length.toString())}</p>
                    <br />
                    <DobSelect
                        key={"move-to-study-container-select-" + this.state.selected} //Needed to rerender
                        className={"file-study-selector"}
                        options={allStudiesThatAreNotUploadedYet}
                        optionSelected={undefined}
                        placeholder={resources.AddToStudy}
                        optionLabel={"label"}
                        optionValue={"studyGuid "}
                        disabled={selectedNonDICOMFiles.length === 0}
                        emptyMessage={resources.NoResultsFound}
                        onChange={this.addSelectedFilesToStudy}
                        formatOptionLabel={option =>
                            this.customItemRendererForStudy({
                                option: option,
                                resources: resources,
                                selectedNonDICOMFilesLength: selectedNonDICOMFiles.length,
                                maxNonDicomFilesAddedToAStudy: maxNonDicomFilesAddedToAStudy,
                            })
                        }
                    />
                    <DobButton color={"neutral"} outline={true} disabled={selectedNonDICOMFiles.length === 0} onClick={this.removeSelectedFiles}>
                        {resources.Remove}
                    </DobButton>
                    <p className={"select-all"} onClick={this.toggleSelectAllFiles}>
                        {supportedNonDICOMFilesToArrange.some(s => !s.selected) ? resources.SelectAll : resources.DeselectAll}
                    </p>
                </div>
            </StaticNotificationComponent>
        );
    }
}
